// ============================================================================
// PlanificationPage — grille de planification salariale director (L05, F-601..611)
// ============================================================================
// SHELL de la page (SL-A) : breadcrumb (F-605), PageHeader, selecteur d'equipe
// (M-652 : portee deja filtree serveur), garde CLIENT Forbidden (M-651), bandeaux
// ?ok=/?error= (deep-links F-604), skeleton/QueryError. SL-C : TeamPlanningView
// (bascule Grille↔Graphique autour du DirectorGridBoard SL-B) + section « coût réel
// courant » (RealPayrollTable) sous la vue.
//
// Portage du PoC (dashboard)/planification-salariale/page.tsx (SSR searchParams)
// au standard maison (SPA + useSearchParams). Gabarit : MonEquipePage (garde
// director-scoped, selecteur GsSegmented) + CommitteeDetailPage (fil d'Ariane).
//
// Garde M-651 : la garde reelle est le 403 SERVEUR (manager ET employee refuses
// en amont de la reponse). Le SPA ne DECIDE pas — il redirige le 403 vers l'ecran
// d'erreur standard /error/403, EN PLUS de la garde serveur (lecon GA-05 : jamais
// recalculer l'autorisation cote client).

import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, GsBreadcrumb, GsSegmented, SkeletonCard, QueryError,
} from '@gs/ui';
import { Users } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { resolvePlanningBack } from '../../lib/nav/back-context';
import { usePlanification, adaptGridClasses } from '../../hooks/usePlanification';
import { TeamPlanningView } from '../../components/salary/TeamPlanningView';
import { RealPayrollTable } from '../../components/salary/RealPayrollTable';

export default function PlanificationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // F-603/604/605 : deep-links. Le SERVEUR decide si `team` est dans la portee
  // (un id hors portee retombe sur la 1re equipe, jamais honore ici).
  const teamParam = searchParams.get('team');
  const okCode = searchParams.get('ok'); // draft_saved / submitted / position_*
  const errorCode = searchParams.get('error'); // forbidden / invalid / notfound / server
  const back = resolvePlanningBack({
    from: searchParams.get('from') ?? undefined,
    member: searchParams.get('member') ?? undefined,
    team: teamParam ?? undefined,
  });

  const q = usePlanification(teamParam, !!user);

  // Garde CLIENT M-651 : le 403 serveur (manager/employee) -> ecran d'erreur
  // standard. La redirection ne REMPLACE pas la garde serveur, elle la double.
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return <Navigate to="/error/403" replace />;
  }

  function selectTeam(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    next.delete('ok');
    next.delete('error');
    setSearchParams(next);
  }

  const data = q.data;
  const selectedTeam = data?.teams.find((team) => team.id === data.selectedTeamId) ?? null;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:planification.title')}
        description={selectedTeam?.name}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
      />

      {/* F-605 : fil d'Ariane de retour contextuel — href whiteliste (anti
          open-redirect, resolvePlanningBack), libelle bilingue par cle i18n.
          Pose SOUS le PageHeader (UI_STANDARDS 1147-1148). */}
      {back ? (
        <GsBreadcrumb
          ariaLabel={t('gest:planification.breadcrumbLabel')}
          LinkComponent={Link}
          items={[
            { label: t(back.labelKey), to: back.href },
            { label: t('gest:planification.title') },
          ]}
        />
      ) : null}

      {q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:planification.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        <>
          {/* F-604 : bandeau ?ok= (succes deep-linkable) — liseré vert SM-5. */}
          {okCode ? (
            <div
              role="status"
              className="rounded-xl border-l-4 border-green-500 bg-green-50 p-4 text-field text-green-700 dark:bg-green-950 dark:text-green-300"
            >
              {t(`gest:planification.ok.${okCode}`, { defaultValue: t('gest:planification.ok.draft_saved') })}
            </div>
          ) : null}

          {/* F-604 : bandeau ?error= mappe i18n (GA-21 : jamais un code brut). */}
          {errorCode ? (
            <div
              role="alert"
              className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4 text-field text-red-700 dark:bg-red-950 dark:text-red-300"
            >
              {t(`gest:planification.errors.${errorCode}`, { defaultValue: t('gest:planification.errors.server') })}
            </div>
          ) : null}

          {/* M-652 : selecteur multi-equipes (visible si >1 equipe dans la PORTEE serveur). */}
          {data.teams.length > 1 ? (
            <GsSegmented
              options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
              value={data.selectedTeamId ?? data.teams[0].id}
              onChange={selectTeam}
              ariaLabel={t('gest:planification.teamSelector')}
              variant="scrollable"
            />
          ) : null}

          {!selectedTeam ? (
            // Aucune equipe dans la portee -> EmptyState (pas une erreur).
            <EmptyState
              icon={<Users size={48} aria-hidden="true" />}
              title={t('gest:planification.empty.title')}
              description={t('gest:planification.empty.description')}
            />
          ) : (
            // SL-C : TeamPlanningView encadre le board d'une bascule Grille↔
            // Graphique (mode graphique = frise agregee M-657, totaux seuls). Sous
            // la vue, la section « coût réel courant » (RealPayrollTable). `classes`
            // est adapte camelCase→snake_case (adaptGridClasses, SL-A) ; le reste du
            // bundle est deja au format snake_case attendu.
            <>
              <TeamPlanningView
                key={selectedTeam.id}
                teamTimeline={data.teamTimeline}
                classes={adaptGridClasses(data.classes)}
                members={data.members}
                currentYear={data.year}
                gridYear={data.gridYear}
                teamId={selectedTeam.id}
                draft={data.draft}
                pending={data.pending}
                lastDecided={data.lastDecided}
                pastByEmail={data.pastByEmail}
                historyByEmail={data.historyByEmail}
                gridYears={data.gridYears}
                realByEmail={data.realByEmail}
                specialHoursByEmail={data.specialHoursByEmail}
              />

              {/* F-610 / M-661 : section « coût réel courant » (ADR-071 §6 point 5 c, dette D2 —
                  JAMAIS « réellement payé »). Filtree a l'equipe (showTeam=false). */}
              {data.realRows.length > 0 ? (
                <section className="space-y-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-chip font-medium uppercase tracking-wide text-slate-400">
                      {t('gest:planification.realSection.eyebrow')}
                    </span>
                    <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
                      {t('gest:planification.realSection.title', {
                        year: data.year,
                        team: selectedTeam.name,
                      })}
                    </h2>
                    <p className="text-field text-slate-500 dark:text-slate-400">
                      {t('gest:planification.realSection.description')}
                    </p>
                  </div>
                  <RealPayrollTable rows={data.realRows} year={data.year} showTeam={false} overtimeRule={data.overtimeRule} />
                </section>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
